


















import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import {
  AddressPositionState,
  GeoLocation,
  GeoLocationConfidence,
  GeocodeOutcome,
  GeocodeResult,
  PickerAddress,
  getAddressPositionIcon,
  getPickedAddressFill,
  getAddressPositionState,
  getLocationConfidence,
  getLocationConfidenceColors,
  getLocationTooltipKeys,
  geocodeAddress,
  isReadyForAutomaticLookup,
  showsConfidenceColor,
} from "@/utils/GeocodingService";
import {
  COLOR_LOCATION_STALE,
  COLOR_LOCATION_STALE_DARK,
} from "@/utils/colors";
import LocationPickerDialogComponent from "@/components/common/LocationPickerDialog.vue";
import { createPlacesLookup } from "@/data/repository/GeocodingRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { i18n } from "@/plugins/vuetify";
import { themed } from "@/utils/Theme";

/**
 * How long to wait after typing stops before spending a lookup.
 *
 * These are billed requests, so the affordance must not fire one per keystroke. Longer than
 * the picker's suggestion debounce: a suggestion list is read while typing continues, while
 * this only has to be right once the user has stopped.
 */
const LOOKUP_DEBOUNCE_MS = 800;

/**
 * The map affordance that rides in an address's house-number field.
 *
 * Four surfaces (user form, company data, branch, damage location) each carried a
 * byte-identical copy of the same pin, tooltip, colour and picker invocation. Extracted so
 * the affordance is defined once: which states it can be in, and what clicking it does, are
 * worth getting right in one place rather than four.
 *
 * It has two jobs beyond opening the map:
 *
 * 1. **It stops vouching for an address that was edited.** A stored position describes the
 *    address it was placed for, so once that address changes the pin marks a different
 *    building. It then greys out and takes a question mark rather than keeping its
 *    confidence colour, which would read as "this address is verified" for a building
 *    nobody entered.
 * 2. **It looks the address up itself.** Once street and house number are both present the
 *    lookup fires on its own, debounced; below that bar the pin stays clickable so a person
 *    can ask for one. Previously nothing in any form ran a lookup at all — the position only
 *    appeared after a save, once IRES Cloud geocoded the record.
 *
 * The picker dialog belongs here rather than to each host. Vuetify's `VDialog` re-attaches
 * its content to the `[data-app]` root, so declaring it inside a text field's `append-outer`
 * slot is safe: the field cannot clip it.
 *
 * Hosts keep their own `enableGeoEncoding` guard in the template. A client with geocoding
 * switched off has no position to look at, so the affordance should not render at all — and
 * not being rendered is also what stops it spending lookups for them.
 */
@Component({
  components: {
    "location-picker-dialog": LocationPickerDialogComponent,
  },
})
export default class AddressPositionField extends Vue {
  /** The stored position for this address, as `v-model`. */
  @Prop({ default: null }) value!: GeoLocation | null;

  /** The address as currently entered in the form. */
  @Prop({ required: true }) address!: PickerAddress;

  /**
   * The address as it was saved, when the host knows it.
   *
   * Lets the affordance tell that the address has been edited since the stored pin was
   * placed, so it can stop vouching for it and the picker can open on the edited address.
   * Absent while creating a record, which has nothing stored to compare against.
   */
  @Prop({ default: undefined }) storedAddress!: PickerAddress | undefined;

  /**
   * Open the picker for viewing only, matching a host surface in view mode.
   *
   * Named `isReadonly` rather than `readonly`, which TypeScript reads as the property
   * modifier and cannot parse as a declared property name.
   */
  @Prop({ default: false }) isReadonly!: boolean;

  isLookingUp = false;

  private lookupTimer: number | null = null;

  /**
   * The address the last automatic lookup was spent on.
   *
   * Guards against re-running for an address already answered: the watcher fires for every
   * keystroke in any of the five fields, and a lookup that returned nothing usable must not
   * be retried on the next unrelated edit.
   */
  private lastLookedUpAddress: string | null = null;

  /**
   * The address the position currently on screen was obtained for, once this component
   * obtained it.
   *
   * Without this the affordance stays stale forever after resolving an edited address: the
   * host's `storedAddress` is still whatever was last *saved*, so the entered address goes on
   * differing from it and the pin keeps the grey question mark it had before the lookup —
   * while every click spends another billed lookup.
   */
  private resolvedAddress: PickerAddress | null = null;

  get state(): AddressPositionState {
    return getAddressPositionState({
      coordinates: this.value,
      entered: this.address,
      // What this component resolved wins over what the host last saved: it is the more recent
      // statement about which address the position describes.
      stored: this.resolvedAddress ?? this.storedAddress,
      isLookingUp: this.isLookingUp,
    });
  }

  get icon(): string {
    return getAddressPositionIcon(this.state, this.value);
  }

  /**
   * Colour of the affordance: the confidence band, or a neutral grey once the address has
   * been edited away from what the position describes.
   */
  get iconColor(): string {
    if (!showsConfidenceColor(this.state)) {
      return themed(COLOR_LOCATION_STALE, COLOR_LOCATION_STALE_DARK);
    }

    const colors = getLocationConfidenceColors(this.value);
    return themed(colors.light, colors.dark);
  }

  /** Tooltip naming how exactly the address is located, and how far to trust it. */
  get tooltip(): string {
    if (this.state === AddressPositionState.STALE) {
      return this.$t("location.addressEdited").toString();
    }

    return getLocationTooltipKeys(this.value)
      .map(key => this.$t(key).toString())
      .join(" · ");
  }

  /**
   * Clicking always opens the map, in every state.
   *
   * It briefly did something cleverer — run a lookup when no position stood — and that was
   * wrong: opening the map is what this control is *for*, and making it conditional took the
   * map away exactly when the address most needed looking at. Nothing is lost by dropping it,
   * because the picker geocodes the address as it opens. That is also the "initiate the search
   * himself" Hans asked for: one click both opens the map and runs the lookup.
   */
  onClick() {
    this.openPicker();
  }

  /**
   * Opens the map so the address can be positioned exactly.
   *
   * When the form's address is still empty and the user placed or picked the pin, the address at
   * the pin fills it in (ticket 2610025), through the same `address-resolved` event as the
   * automatic backfill. See {@link getPickedAddressFill} for why a typed address is never replaced.
   */
  async openPicker() {
    const picker = this.$refs.locationPicker as LocationPickerDialogComponent;

    const result = await picker.show(
      this.address,
      this.value,
      this.isReadonly,
      this.storedAddress
    );

    if (result) {
      const fill = getPickedAddressFill(this.address, result.address);

      // The pin was placed for the address the picker was opened with, plus whatever it fills in,
      // so it is no longer the saved address that decides whether it is stale.
      this.resolvedAddress = { ...this.address, ...fill };
      this.$emit("input", result.position);

      if (fill) {
        this.$emit("address-resolved", fill);
      }
    }
  }

  /**
   * Schedules an automatic lookup when the edited address is complete enough for one.
   *
   * Deep, because the hosts pass a fresh object built from their own fields: the identity
   * changes on every render, so only the values can say whether anything moved.
   */
  @Watch("address", { deep: true })
  onAddressChanged() {
    this.cancelPendingLookup();

    if (this.isReadonly || !isReadyForAutomaticLookup(this.address)) {
      return;
    }

    if (this.addressKey === this.lastLookedUpAddress) {
      return;
    }

    // Only worth spending on an address nothing currently vouches for. A position that still
    // describes what is on screen is already the answer.
    if (this.state === AddressPositionState.POSITIONED) {
      return;
    }

    this.lookupTimer = window.setTimeout(() => this.runLookup(), LOOKUP_DEBOUNCE_MS);
  }

  /**
   * Looks the entered address up and adopts the position it produces.
   *
   * A failed or unmatched lookup deliberately leaves the existing value alone rather than
   * clearing it: the stored pin is what is actually on the record, and dropping it would lose
   * a hand-placed position to a momentary network error.
   */
  private async runLookup() {
    this.cancelPendingLookup();
    this.lastLookedUpAddress = this.addressKey;
    this.isLookingUp = true;

    try {
      const result = await geocodeAddress(this.address, {
        language: i18n.locale,
        // Goes through IRES Cloud, so it is available here exactly as it is in the picker. It
        // used to depend on the Maps script already being loaded, which meant a form in a
        // session where no map had been opened resolved an address worse than the map did.
        places: createPlacesLookup(i18n.locale),
      });

      if (result.outcome === GeocodeOutcome.OK && result.coordinates) {
        if (this.wouldDowngrade(result.coordinates)) {
          // Nothing usable came back and something already stands. Replacing a real position
          // with a locality centre makes the record worse than not looking at all, which is
          // what this path did before it ran lookups of its own.
          return;
        }

        this.resolvedAddress = { ...this.address };
        this.$emit("input", result.coordinates);
        this.backfillMissingParts(result);
      }
    } finally {
      this.isLookingUp = false;
    }
  }

  /**
   * Offers the host the postcode and city the lookup found, for the ones the user left out.
   *
   * Only what is missing, and never what was typed: a user who entered a postcode meant it,
   * even if Google disagrees, and silently correcting it would be a different feature. The
   * host decides what to do with them, since only it knows which fields these are.
   *
   * Emitted from the automatic lookup, where the address is exactly what was typed. A position
   * dragged around the map implies nothing about a typed postcode, so the picker fills only an
   * address that is still empty ({@link openPicker}).
   */
  private backfillMissingParts(result: GeocodeResult) {
    const isMissing = (value: string | null | undefined) =>
      (value ?? "").trim().length === 0;

    const parts: { zipCode?: string; city?: string } = {
      ...(isMissing(this.address.zipCode)
        && result.resolvedPostalCode
        && { zipCode: result.resolvedPostalCode }),
      ...(isMissing(this.address.city)
        && result.resolvedCity
        && { city: result.resolvedCity }),
    };

    if (Object.keys(parts).length === 0) {
      return;
    }

    // Fold them into the address this position was obtained for. They came out of the very
    // result being stored, so filling them in does not describe a different building — and
    // without this the pin turns grey the instant it backfills anything, and schedules a second
    // lookup for an address it has just answered.
    this.resolvedAddress = { ...this.address, ...parts };

    this.$emit("address-resolved", parts);

    // Named rather than silent: fields filling themselves in while someone types reads as a
    // glitch unless something says where the values came from.
    SnackbarModule.showSuccess({
      key: "location.backfilled",
      params: [[parts.zipCode, parts.city].filter(Boolean).join(" ")],
    });
  }

  /**
   * Whether adopting this result would throw away a better position than it offers.
   *
   * Only the bottom band counts as a downgrade. Anything above it is a real answer for the
   * address on screen and should replace a pin placed for a different one, which is the
   * 2026-08-18 rule that an edited address gives up its old position.
   */
  private wouldDowngrade(candidate: GeoLocation): boolean {
    return (
      !!this.value
      && getLocationConfidence(candidate) === GeoLocationConfidence.UNRELIABLE
      && getLocationConfidence(this.value) !== GeoLocationConfidence.UNRELIABLE
    );
  }

  /** The entered address reduced to one comparable string. */
  private get addressKey(): string {
    return [
      this.address.street,
      this.address.houseNumber,
      this.address.zipCode,
      this.address.city,
      this.address.country,
    ]
      .map(part => (part ?? "").trim())
      .join("|");
  }

  private cancelPendingLookup() {
    if (this.lookupTimer !== null) {
      window.clearTimeout(this.lookupTimer);
      this.lookupTimer = null;
    }
  }

  beforeDestroy() {
    this.cancelPendingLookup();
  }
}
